import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

interface SystemSettings {
  maintenanceMode: boolean;
  registrationEnabled: boolean;
  twoFactorRequired: boolean;
  sessionTimeout: number; // in minutes
  maxUsers: number;
  emailNotifications: boolean;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
  logRetentionDays: number;
}

interface AdminSettingsProps {
  onBack: () => void;
}

const AdminSettings: React.FC<AdminSettingsProps> = ({ onBack }) => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState<SystemSettings>({
    maintenanceMode: false,
    registrationEnabled: true,
    twoFactorRequired: false,
    sessionTimeout: 60,
    maxUsers: 1000,
    emailNotifications: true,
    backupFrequency: 'daily',
    logRetentionDays: 30
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadSettings();
    }
  }, [currentUser]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Loading system settings...');

      // Try to load settings from Firestore
      const settingsDoc = await getDoc(doc(db, 'admin', 'settings'));
      
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSettings({
          maintenanceMode: data.maintenanceMode || false,
          registrationEnabled: data.registrationEnabled !== false, // default to true
          twoFactorRequired: data.twoFactorRequired || false,
          sessionTimeout: data.sessionTimeout || 60,
          maxUsers: data.maxUsers || 1000,
          emailNotifications: data.emailNotifications !== false, // default to true
          backupFrequency: data.backupFrequency || 'daily',
          logRetentionDays: data.logRetentionDays || 30
        });
        console.log('Loaded settings:', data);
      } else {
        console.log('No settings found, using defaults');
        // Settings will use the default values from useState
      }
    } catch (error: any) {
      console.error('Error loading settings:', error);
      setError('Failed to load settings. Using default values.');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Save settings to Firestore
      await setDoc(doc(db, 'admin', 'settings'), {
        ...settings,
        lastUpdated: new Date(),
        updatedBy: currentUser?.uid
      });

      setSuccess('Settings saved successfully!');
      console.log('Settings saved:', settings);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Error saving settings:', error);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key: keyof SystemSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values?')) {
      setSettings({
        maintenanceMode: false,
        registrationEnabled: true,
        twoFactorRequired: false,
        sessionTimeout: 60,
        maxUsers: 1000,
        emailNotifications: true,
        backupFrequency: 'daily',
        logRetentionDays: 30
      });
      setSuccess('Settings reset to defaults');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-600 text-white p-4 rounded-lg mb-6">
            <h2 className="text-xl font-bold">Access Denied</h2>
            <p>You must be an admin to access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-700 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">System Settings</h1>
              <p className="text-purple-100">Configure system behavior and preferences</p>
            </div>
            <button
              onClick={onBack}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-md transition-all duration-200"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-600 text-white p-4 rounded-lg mb-6">
            <p>{error}</p>
            <button
              onClick={() => setError('')}
              className="mt-2 text-red-200 hover:text-white underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {success && (
          <div className="bg-green-600 text-white p-4 rounded-lg mb-6">
            <p>{success}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>Loading settings...</p>
          </div>
        )}

        {/* Settings Form */}
        {!loading && (
          <div className="space-y-6">
            {/* System Status */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <span className="mr-2">⚙️</span>
                System Status
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Maintenance Mode</label>
                    <p className="text-xs text-gray-400">Disable user access during maintenance</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.maintenanceMode}
                      onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Registration Enabled</label>
                    <p className="text-xs text-gray-400">Allow new user registrations</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.registrationEnabled}
                      onChange={(e) => handleSettingChange('registrationEnabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Security Settings */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <span className="mr-2">🔒</span>
                Security Settings
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Require 2FA</label>
                    <p className="text-xs text-gray-400">Force two-factor authentication for all users</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.twoFactorRequired}
                      onChange={(e) => handleSettingChange('twoFactorRequired', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Session Timeout (minutes)</label>
                    <p className="text-xs text-gray-400">Auto-logout after inactivity</p>
                  </div>
                  <input
                    type="number"
                    min="5"
                    max="1440"
                    value={settings.sessionTimeout}
                    onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                    className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-1 text-sm focus:outline-none focus:border-blue-500 w-20"
                  />
                </div>
              </div>
            </div>

            {/* User Limits */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <span className="mr-2">👥</span>
                User Limits
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Maximum Users</label>
                    <p className="text-xs text-gray-400">Limit total number of registered users</p>
                  </div>
                  <input
                    type="number"
                    min="1"
                    max="10000"
                    value={settings.maxUsers}
                    onChange={(e) => handleSettingChange('maxUsers', parseInt(e.target.value))}
                    className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-1 text-sm focus:outline-none focus:border-blue-500 w-24"
                  />
                </div>
              </div>
            </div>

            {/* Notification Settings */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <span className="mr-2">📧</span>
                Notification Settings
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Email Notifications</label>
                    <p className="text-xs text-gray-400">Send system notifications via email</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.emailNotifications}
                      onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Data Management */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <span className="mr-2">💾</span>
                Data Management
              </h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Backup Frequency</label>
                    <p className="text-xs text-gray-400">How often to backup system data</p>
                  </div>
                  <select
                    value={settings.backupFrequency}
                    onChange={(e) => handleSettingChange('backupFrequency', e.target.value)}
                    className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-1 text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-300">Log Retention (days)</label>
                    <p className="text-xs text-gray-400">How long to keep system logs</p>
                  </div>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={settings.logRetentionDays}
                    onChange={(e) => handleSettingChange('logRetentionDays', parseInt(e.target.value))}
                    className="bg-gray-700 text-white border border-gray-600 rounded px-3 py-1 text-sm focus:outline-none focus:border-blue-500 w-20"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={resetToDefaults}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-2 rounded-md transition-all duration-200"
                >
                  Reset to Defaults
                </button>
                <div className="flex space-x-4">
                  <button
                    onClick={loadSettings}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-md transition-all duration-200"
                  >
                    Reload Settings
                  </button>
                  <button
                    onClick={saveSettings}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-500 text-white px-6 py-2 rounded-md transition-all duration-200 flex items-center space-x-2"
                  >
                    {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                    <span>{saving ? 'Saving...' : 'Save Settings'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;












